import { useMemo, useState } from 'react';
import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import {
  Phase,
  PhaseMetrics,
  PHASE_LABELS,
  TraceReliabilityIndicator,
} from '../../api/types';
import { ReliabilityBadge } from '../ReliabilityBadge';

export interface MetricsPanelProps {
  metrics: PhaseMetrics[];
  selectedPhase: Phase;
  layout?: 'vertical' | 'horizontal';
  embedded?: boolean;
}

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(0.5, 1),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.75, 0.5),
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
  content: {
    padding: theme.spacing(1.25),
  },
  label: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  value: {
    fontSize: '0.85rem',
  },
  valueMuted: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  horizontalContent: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1, 3),
    padding: theme.spacing(1, 2),
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5, 1.5),
      padding: theme.spacing(1),
    },
  },
  horizontalItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'normal',
    },
  },
  horizontalLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  horizontalValue: {
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  valueWithState: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
}));

function formatCost(cost: number): string {
  return `$${cost.toFixed(4)}`;
}

function formatLatency(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatTokens(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function buildMetricFingerprint(metric: PhaseMetrics): string {
  return [
    metric.phase,
    metric.model,
    metric.inputTokens,
    metric.outputTokens,
    metric.totalTokens,
    metric.estimatedCost.toFixed(6),
    metric.latencyMs,
    metric.agentRole,
    metric.startedAt,
  ].join('|');
}

function hasVisibleTelemetry(metrics: {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  estimatedCost: number;
  latencyMs: number;
}): boolean {
  return (
    metrics.inputTokens > 0 ||
    metrics.outputTokens > 0 ||
    metrics.totalTokens > 0 ||
    metrics.estimatedCost > 0 ||
    metrics.latencyMs > 0
  );
}

function getAggregatedLabel(values: string[], pluralLabel: string): string {
  if (values.length === 0) {
    return '-';
  }

  if (values.length === 1) {
    return values[0];
  }

  return `${values.length} ${pluralLabel}`;
}

function buildPhaseReliability(
  metrics: PhaseMetrics[],
  telemetryVisible: boolean,
): Record<string, TraceReliabilityIndicator> {
  const coverage = `${metrics.length} execução(ões) observada(s)`;
  const period = getObservedPeriod(metrics);
  const updatedAt = latestTimestamp(metrics);

  return {
    tokens: buildIndicator(telemetryVisible ? 'calculated' : 'unavailable', {
      coverage,
      period,
      updatedAt,
      explanation: 'Tokens somados a partir das métricas persistidas da fase.',
    }),
    cost: buildIndicator(telemetryVisible ? 'estimated' : 'unavailable', {
      coverage,
      period,
      updatedAt,
      currency: 'USD da fonte',
      explanation: 'Custo estimado em USD informado pela fonte da métrica.',
    }),
    latency: buildIndicator(telemetryVisible ? 'calculated' : 'unavailable', {
      coverage,
      period,
      updatedAt,
      explanation: 'Latência somada sobre a janela observada da fase.',
    }),
    executions: buildIndicator(
      metrics.length > 0 ? 'confirmed' : 'unavailable',
      {
        coverage,
        period,
        updatedAt,
        explanation: 'Contagem de execuções persistidas para a fase.',
      },
    ),
  };
}

function buildIndicator(
  state: TraceReliabilityIndicator['state'],
  input: Omit<TraceReliabilityIndicator, 'state' | 'label' | 'source'>,
): TraceReliabilityIndicator {
  const labels: Record<TraceReliabilityIndicator['state'], string> = {
    confirmed: 'Confirmado',
    calculated: 'Calculado',
    estimated: 'Estimado',
    partial: 'Parcial',
    unavailable: 'Indisponível',
    'not-applicable': 'N/A',
  };

  return {
    ...input,
    state,
    label: labels[state],
    source: 'Métricas da fase',
  };
}

function getObservedPeriod(metrics: PhaseMetrics[]): string {
  if (metrics.length === 0) {
    return 'Janela observada: sem registros';
  }

  const starts = metrics.map(metric => metric.startedAt).sort();
  const ends = metrics
    .map(metric => metric.completedAt ?? metric.startedAt)
    .sort();
  return `Janela observada: ${starts[0]} até ${ends[ends.length - 1]}`;
}

function latestTimestamp(metrics: PhaseMetrics[]): string | null {
  const timestamps = metrics
    .map(metric => metric.completedAt ?? metric.startedAt)
    .sort();
  return timestamps[timestamps.length - 1] ?? null;
}

function MetricValue({
  value,
  indicator,
}: {
  value: React.ReactNode;
  indicator: TraceReliabilityIndicator;
}) {
  const classes = useStyles();

  return (
    <span className={classes.valueWithState}>
      <span>{value}</span>
      <ReliabilityBadge indicator={indicator} />
    </span>
  );
}

export const MetricsPanel = ({
  metrics,
  selectedPhase,
  layout = 'vertical',
  embedded: _embedded = false,
}: MetricsPanelProps) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(layout !== 'horizontal');

  const phaseMetrics = useMemo(() => {
    const unique = new Map<string, PhaseMetrics>();
    for (const metric of metrics) {
      if (metric.phase !== selectedPhase) {
        continue;
      }
      const fingerprint = buildMetricFingerprint(metric);
      if (!unique.has(fingerprint)) {
        unique.set(fingerprint, metric);
      }
    }
    return [...unique.values()];
  }, [metrics, selectedPhase]);

  const aggregatedMetrics = useMemo(() => {
    if (phaseMetrics.length === 0) {
      return null;
    }

    const models = Array.from(
      new Set(
        phaseMetrics
          .map(metric => metric.model)
          .filter(model => model && model.trim().length > 0),
      ),
    );
    const agentRoles = Array.from(
      new Set(
        phaseMetrics
          .map(metric => metric.agentRole)
          .filter(role => role && role.trim().length > 0),
      ),
    );

    return {
      modelLabel: getAggregatedLabel(models, 'modelos'),
      agentRoleLabel: getAggregatedLabel(
        agentRoles.map(role => PHASE_LABELS[role as Phase] || role),
        'agentes',
      ),
      inputTokens: phaseMetrics.reduce(
        (total, metric) => total + metric.inputTokens,
        0,
      ),
      outputTokens: phaseMetrics.reduce(
        (total, metric) => total + metric.outputTokens,
        0,
      ),
      totalTokens: phaseMetrics.reduce(
        (total, metric) => total + metric.totalTokens,
        0,
      ),
      estimatedCost: phaseMetrics.reduce(
        (total, metric) => total + metric.estimatedCost,
        0,
      ),
      latencyMs: phaseMetrics.reduce(
        (total, metric) => total + metric.latencyMs,
        0,
      ),
      executions: phaseMetrics.length,
    };
  }, [phaseMetrics]);
  const telemetryVisible = aggregatedMetrics
    ? hasVisibleTelemetry(aggregatedMetrics)
    : false;
  const reliability = useMemo(
    () => buildPhaseReliability(phaseMetrics, telemetryVisible),
    [phaseMetrics, telemetryVisible],
  );

  const toggleHeader = (
    <div
      className={classes.header}
      onClick={() => setExpanded(prev => !prev)}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          setExpanded(prev => !prev);
        }
      }}
    >
      <Typography variant="subtitle2">
        Métricas - {PHASE_LABELS[selectedPhase]}
      </Typography>
      <IconButton
        size="small"
        aria-label={expanded ? 'Recolher métricas' : 'Expandir métricas'}
      >
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </div>
  );

  if (layout === 'horizontal') {
    return (
      <div className={classes.root}>
        {toggleHeader}
        <Collapse in={expanded}>
          {!aggregatedMetrics ? (
            <div className={classes.emptyState}>
              <Typography variant="body2">
                Nenhuma métrica disponível para esta fase
              </Typography>
            </div>
          ) : (
            <div className={classes.horizontalContent}>
              <div className={classes.horizontalItem}>
                <Typography className={classes.horizontalLabel}>
                  Modelo:
                </Typography>
                <Typography className={classes.horizontalValue}>
                  {aggregatedMetrics.modelLabel}
                </Typography>
              </div>
              <div className={classes.horizontalItem}>
                <Typography className={classes.horizontalLabel}>
                  Tokens (In/Out):
                </Typography>
                <Typography className={classes.horizontalValue}>
                  <MetricValue
                    value={
                      telemetryVisible
                        ? `${formatTokens(
                            aggregatedMetrics.inputTokens,
                          )} / ${formatTokens(aggregatedMetrics.outputTokens)}`
                        : 'Indisponível'
                    }
                    indicator={reliability.tokens}
                  />
                </Typography>
              </div>
              <div className={classes.horizontalItem}>
                <Typography className={classes.horizontalLabel}>
                  Total:
                </Typography>
                <Typography className={classes.horizontalValue}>
                  <MetricValue
                    value={
                      telemetryVisible
                        ? formatTokens(aggregatedMetrics.totalTokens)
                        : 'Indisponível'
                    }
                    indicator={reliability.tokens}
                  />
                </Typography>
              </div>
              <div className={classes.horizontalItem}>
                <Typography className={classes.horizontalLabel}>
                  Custo:
                </Typography>
                <Typography className={classes.horizontalValue}>
                  <MetricValue
                    value={
                      telemetryVisible
                        ? formatCost(aggregatedMetrics.estimatedCost)
                        : 'Indisponível'
                    }
                    indicator={reliability.cost}
                  />
                </Typography>
              </div>
              <div className={classes.horizontalItem}>
                <Typography className={classes.horizontalLabel}>
                  Latência:
                </Typography>
                <Typography className={classes.horizontalValue}>
                  <MetricValue
                    value={
                      telemetryVisible
                        ? formatLatency(aggregatedMetrics.latencyMs)
                        : 'Indisponível'
                    }
                    indicator={reliability.latency}
                  />
                </Typography>
              </div>
              <div className={classes.horizontalItem}>
                <Typography className={classes.horizontalLabel}>
                  Execuções:
                </Typography>
                <Typography className={classes.horizontalValue}>
                  <MetricValue
                    value={aggregatedMetrics.executions}
                    indicator={reliability.executions}
                  />
                </Typography>
              </div>
            </div>
          )}
        </Collapse>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {toggleHeader}
      <Collapse in={expanded}>
        <div className={classes.content}>
          {!aggregatedMetrics ? (
            <div className={classes.emptyState}>
              <Typography variant="body2">
                Nenhuma métrica disponível para esta fase
              </Typography>
            </div>
          ) : (
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell className={classes.label}>Modelo</TableCell>
                  <TableCell className={classes.value}>
                    {aggregatedMetrics.modelLabel}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.label}>
                    Role do Agente
                  </TableCell>
                  <TableCell className={classes.value}>
                    {aggregatedMetrics.agentRoleLabel}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.label}>Execuções</TableCell>
                  <TableCell className={classes.value}>
                    <MetricValue
                      value={aggregatedMetrics.executions}
                      indicator={reliability.executions}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.label}>
                    Tokens (In/Out)
                  </TableCell>
                  <TableCell
                    className={
                      telemetryVisible ? classes.value : classes.valueMuted
                    }
                  >
                    <MetricValue
                      value={
                        telemetryVisible
                          ? `${formatTokens(
                              aggregatedMetrics.inputTokens,
                            )} / ${formatTokens(
                              aggregatedMetrics.outputTokens,
                            )}`
                          : 'Indisponível'
                      }
                      indicator={reliability.tokens}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.label}>Total Tokens</TableCell>
                  <TableCell
                    className={
                      telemetryVisible ? classes.value : classes.valueMuted
                    }
                  >
                    <MetricValue
                      value={
                        telemetryVisible
                          ? formatTokens(aggregatedMetrics.totalTokens)
                          : 'Indisponível'
                      }
                      indicator={reliability.tokens}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.label}>
                    Custo Estimado
                  </TableCell>
                  <TableCell
                    className={
                      telemetryVisible ? classes.value : classes.valueMuted
                    }
                  >
                    <MetricValue
                      value={
                        telemetryVisible
                          ? formatCost(aggregatedMetrics.estimatedCost)
                          : 'Indisponível'
                      }
                      indicator={reliability.cost}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.label}>Latência</TableCell>
                  <TableCell
                    className={
                      telemetryVisible ? classes.value : classes.valueMuted
                    }
                  >
                    <MetricValue
                      value={
                        telemetryVisible
                          ? formatLatency(aggregatedMetrics.latencyMs)
                          : 'Indisponível'
                      }
                      indicator={reliability.latency}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </div>
      </Collapse>
    </div>
  );
};
